/**
 * Kids hairstyles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_hair_looks`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const kidsHairstylesPack: ToilePack = {
    slug: 'kids-hairstyles',
    category: 'beauty',
    name: 'Kids hairstyles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'spacebuns', label: 'Space buns', subject: "two small round buns high on either side of a child's head, a zigzag part between them"},
        {key: 'beads', label: 'Cornrows with beads', subject: "a child's straight-back cornrows ending in bright coloured beads that click at the shoulders"},
        {key: 'sidebraid', label: 'Side braid', subject: "a child's loose side braid with two small star clips above the ear, the face smiling"},
    ],
    styles: [
        {key: 'home', label: 'At home', style: 'a soft candid photograph at home in morning window light', palette: 'Natural hair tones and a pastel t-shirt against a pale bedroom wall.', framing: 'Head and shoulders in a three-quarter view, the room dropped soft behind.'},
        {key: 'back', label: 'Back view', style: 'a back-view hair reference photograph showing the whole style from crown to ends', palette: 'Hair and accessories in true color against a plain pale wall.', framing: 'Seen from behind at head height, the style centered with even space around it.'},
        {key: 'park', label: 'In the park', style: 'a candid photograph outdoors in a park on a sunny afternoon, the child mid-laugh', palette: 'Bright accessory colors, green grass and trees blurred behind.', framing: 'Waist-up, the child off-center with the park falling away in shallow depth.'},
    ],
    directions: ['for a toddler', 'for boys', 'rainbow bows'],
    directionHint: 'try: for a toddler',
    seo: {
        title: 'Kids hairstyles: space buns, cornrows with beads, side braid',
        metaDescription: 'Nine kids hairstyle pictures: two space buns, cornrows with coloured beads and a side braid with star clips, in three settings.',
        h1: 'Kids hairstyles: space buns, cornrows with beads, side braid with clips',
        tagline: 'Three styles for children, each shown at home, from behind and in the park.',
        intro: 'Styles a child will sit still for: two small round buns high on the head with a zigzag part, straight-back cornrows ending in bright coloured beads, and a loose side braid held with star clips. Each is shown at home in morning light, from behind so the whole style is visible, and in a park on a sunny afternoon. Type a detail, such as rainbow bows, to ask for that version.',
        useCases: [
            "a style for a toddler's first hairdo",
            'a cornrow style with beads',
            'a braid for a park day',
            'a picture to show a grandparent who does the hair',
        ],
        faq: [
            {q: 'Can I ask for these styles with bows?', a: 'Yes. A detail you type is a request for that change, not a promised result, and the three styles are shown in that form.'},
            {q: 'What is the difference between the three styles?', a: 'The space buns sit high on either side, the cornrows run straight back with beads at the ends, and the side braid is loose with star clips above the ear.'},
            {q: 'Are these real photographs of people?', a: 'No. They are generated images made to show each style, not photographs of a named person.'},
        ],
    },
}
